import apiService from 'common/services/api/apiService';
import FormVerificationResponse from 'domain/components/verify-identity/FormVerificationResponse';
import DwellingTypes from 'domain/enums/DwellingTypes';
import ChsAttachment, {
  VerificationUploadType,
} from 'domain/types/ChsAttachment';
import ChsIntermediary from 'domain/types/ChsIntermediary';
import { GroupReferenceKey } from 'features/cart/CartSlice';

export interface FileToUpload {
  files: File;
  attachmentTypeCd: string;
  uploadTypeCd: VerificationUploadType;
}

export const getAttachment = (
  verification: FormVerificationResponse | null,
  type: VerificationUploadType
): ChsAttachment | null =>
  (verification &&
    verification.primaryResAttachment &&
    verification.primaryResAttachment.find((a) => a.uploadType === type)) ||
  null;

/**
 * @param groupRefKey the Group Reference Key
 * @param accessToken your access token
 * @param file the file you would like to upload
 * @param attachmentTypeCd the attachhment type (e.g. passport, license)
 * @returns Rejects with a string message on failure, otherwise resolve(true) on success.
 */
export default async function upload(
  groupRefKey: GroupReferenceKey,
  accessToken: string,
  endpoint: string,
  isPrimaryResidence: boolean,
  dwellingTypeCd: string | null,
  howManyRooms: number | null,
  intermediaries?: ChsIntermediary[],
  relationshipCd?: string,
  attachments?: FileToUpload[]
) {
  if (!groupRefKey) {
    return Promise.reject('Invalid group reference key.');
  }
  if (!accessToken) {
    return Promise.reject('Cannot make unauthenticated request.');
  }

  const formData = new FormData();
  // Mandatory fields
  formData.append('groupRefKey', groupRefKey);
  formData.append('primaryResident', String(isPrimaryResidence));

  if (dwellingTypeCd) {
    formData.append('dwellingTypeCd', dwellingTypeCd);
  }

  // Optional fields

  if (
    dwellingTypeCd === DwellingTypes.COMBINATION ||
    dwellingTypeCd === DwellingTypes.PARTIAL
  ) {
    formData.append('howManyRooms', String(howManyRooms));
  }

  if (isPrimaryResidence && attachments) {
    for (let i = 0; i < attachments.length; i++) {
      formData.append(`attachments[${i}].files`, attachments[i].files);
      formData.append(
        `attachments[${i}].uploadTypeCd`,
        attachments[i].uploadTypeCd
      );
      formData.append(
        `attachments[${i}].attachmentTypeCd`,
        attachments[i].attachmentTypeCd
      );
    }
  }

  if (intermediaries && intermediaries.length > 0) {
    formData.append(
      'interMediaryCds',
      intermediaries.map((t) => t.cd).join(',')
    );
  }

  if (relationshipCd) {
    formData.append('relationShipCD', relationshipCd);
  }

  const response = await apiService.postFile(endpoint, accessToken, formData);
  const data = await response.json();
  if (data && data.status >= 400 && data.error) {
    return Promise.reject(getCustomError(data));
  } else {
    return Promise.resolve(true);
  }
}

function getCustomError(data?: { error?: string; message?: string }) {
  if (data) {
    if (data.message) {
      return data.message;
    } else if (data.error) {
      return data.error;
    }
  }
  return 'Unhandled error.';
}
